// ─────────────────────────────────────────────────────────────────────────────
// Helpers de Supabase Storage pro bucket `fluxo-caixa-anexos` (privado).
//
// O arquivo fica no Storage; os metadados (nome original, tamanho, autor,
// uploaded_at, path) ficam no array `anexos` do próprio lançamento dentro
// de fluxoCaixa[]. Isso evita inflar o JSONB com base64 e respeita o
// orçamento de egress do diary.
//
// Path no bucket: anexos-fluxo/{lancamentoId}/{uuid}-{nomeSanitizado}.pdf
// ─────────────────────────────────────────────────────────────────────────────

import { supabase } from './supabase';

const BUCKET = 'fluxo-caixa-anexos';
const MAX_BYTES = 10 * 1024 * 1024; // 10 MB
const SIGNED_URL_TTL = 60 * 60;     // 1h é suficiente pra abrir o PDF

// Remove acento, espaço e caractere especial pra evitar erro do Storage
function sanitizar(nome) {
  return nome
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .slice(0, 80);
}

function uuid() {
  return (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
}

/**
 * Faz upload de um File (PDF) e devolve metadata pra salvar no lançamento.
 *
 * @param {File} file - arquivo escolhido pelo user
 * @param {string|number} lancamentoId - id do lançamento dono do anexo
 * @param {string} [autorEmail] - email do user logado, vai pra metadata
 * @returns {Promise<{ id, path, nome, tamanho, mime, uploaded_at, uploaded_by }>}
 */
export async function uploadAnexo(file, lancamentoId, autorEmail = null) {
  if (!file) throw new Error('Arquivo não informado.');
  if (file.type !== 'application/pdf') {
    throw new Error('Apenas arquivos PDF são aceitos.');
  }
  if (file.size > MAX_BYTES) {
    throw new Error(`Arquivo muito grande (${(file.size / 1024 / 1024).toFixed(1)} MB). Máximo 10 MB.`);
  }

  // Pega email do user logado se não foi passado
  if (!autorEmail) {
    try {
      const { data } = await supabase.auth.getUser();
      autorEmail = data?.user?.email || null;
    } catch { /* segue sem autor */ }
  }

  const id = uuid();
  const path = `anexos-fluxo/${lancamentoId}/${id}-${sanitizar(file.name)}`;

  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: 'application/pdf',
    });

  if (error) {
    throw new Error(`Falha no upload: ${error.message}`);
  }

  return {
    id,
    path,
    nome: file.name,
    tamanho: file.size,
    mime: file.type,
    uploaded_at: new Date().toISOString(),
    uploaded_by: autorEmail,
  };
}

/**
 * Gera URL assinada temporária pra visualizar o PDF.
 * @param {string} path - path do anexo no bucket
 * @returns {Promise<string>} URL com expiração de 1h
 */
export async function getSignedUrl(path) {
  if (!path) throw new Error('Path não informado.');
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL);

  if (error) {
    throw new Error(`Falha ao gerar URL: ${error.message}`);
  }
  return data.signedUrl;
}

/**
 * Remove o arquivo do Storage. Não atualiza o lançamento — caller faz isso.
 * @param {string} path
 */
export async function deleteAnexo(path) {
  if (!path) return;
  const { error } = await supabase.storage.from(BUCKET).remove([path]);
  if (error) {
    throw new Error(`Falha ao excluir: ${error.message}`);
  }
}

/**
 * Formata tamanho em KB/MB pra exibir na UI.
 */
export function fmtTamanho(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
